import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  TextField,
  Button,
  Divider
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';

import carebeyondLogo from '../assets/carebeyondsidebar.png';
import BookDemoModal from './BookDemoModal';

const TEAL = '#0B6B6B';
const TEAL_DARK = '#094F4F';
const NAVY = '#1A2F3A';

export default function Footer({ onOpenDemo }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [demoOpen, setDemoOpen] = useState(false);
  const [contactData, setContactData] = useState({ name: '', email: '', message: '' });
  const [contactSent, setContactSent] = useState(false);

  const handleOpenDemo = () => {
    if (onOpenDemo) {
      onOpenDemo();
    } else {
      setDemoOpen(true);
    }
  };

  const handleNavClick = (href) => {
    if (href === '/') {
      if (location.pathname === '/') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate('/');
      }
    } else if (href.startsWith('#')) {
      if (location.pathname !== '/') {
        navigate('/', { state: { scrollTo: href } });
        setTimeout(() => {
          const el = document.querySelector(href);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      } else {
        const el = document.querySelector(href);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    } else {
      navigate(href);
    }
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (!contactData.name || !contactData.email || !contactData.message) return;
    setContactSent(true);
    setContactData({ name: '', email: '', message: '' });
  };

  const QUICK_LINKS = [
    { label: 'Home', href: '/' },
    { label: 'For Doctors', href: '/for-doctors' },
    { label: 'Features', href: '#features' },
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Book a Demo', action: handleOpenDemo },
    { label: 'Security', href: '/security' },
    { label: 'Contact Us', href: '/contact' }
  ];

  const FEATURE_LINKS = [
    { label: 'Patient Management', href: '#features' },
    { label: 'Appointments', href: '#features' },
    { label: 'Prescriptions', href: '#features' },
    { label: 'Medicine Reminders', href: '#features' },
    { label: 'Vitals Monitoring', href: '#features' },
    { label: 'Emergency Alerts', href: '#features' },
    { label: 'Reports & Analytics', href: '#features' }
  ];

  const SOCIAL_LINKS = [
    { name: 'Facebook', icon: <FacebookIcon sx={{ fontSize: 18 }} />, url: 'https://facebook.com' },
    { name: 'LinkedIn', icon: <LinkedInIcon sx={{ fontSize: 18 }} />, url: 'https://linkedin.com' },
    { name: 'Twitter', icon: <TwitterIcon sx={{ fontSize: 18 }} />, url: 'https://twitter.com' },
    { name: 'YouTube', icon: <YouTubeIcon sx={{ fontSize: 18 }} />, url: 'https://youtube.com' }
  ];

  return (
    <>
      <Box component="footer" sx={{ backgroundColor: NAVY, pt: { xs: 7, sm: 9, md: 12 }, pb: 4, color: '#ffffff' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 3, md: 8 } }}>
          <Grid container spacing={{ xs: 5, sm: 4, md: 6 }} sx={{ mb: 6 }}>
            {/* Brand column */}
            <Grid size={{ xs: 12, md: 3 }}>
              <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', mb: 2.5, cursor: 'pointer' }} onClick={() => handleNavClick('/')}>
                <Box
                  component="img"
                  src={carebeyondLogo}
                  alt="Care Beyond"
                  sx={{
                    height: { xs: '32px', md: '38px' },
                    width: 'auto',
                    objectFit: 'contain',
                    filter: 'brightness(0) invert(1)',
                    transition: 'transform 0.2s ease',
                    '&:hover': { transform: 'scale(1.02)' }
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: { xs: '8.5px', md: '10px' },
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.6)',
                    letterSpacing: '0.3px',
                    mt: 0.3,
                    whiteSpace: 'nowrap'
                  }}
                >
                  By VDepth Technologies Private Limited
                </Typography>
              </Box>
              <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: '13px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, mb: 3 }}>
                We help doctors build stronger relationships with their patients through intelligent automation and meaningful engagement.
              </Typography>
              <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap>
                {SOCIAL_LINKS.map((s, i) => (
                  <Box
                    key={i}
                    component="a"
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#ffffff',
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': { backgroundColor: TEAL, color: '#ffffff', transform: 'translateY(-2px)' }
                    }}
                    aria-label={s.name}
                  >
                    {s.icon}
                  </Box>
                ))}
              </Stack>
            </Grid>

            {/* Quick Links */}
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography sx={{ fontFamily: 'Outfit', fontWeight: 700, fontSize: '15px', color: '#ffffff', mb: 2.5 }}>Quick Links</Typography>
              {QUICK_LINKS.map((item) => (
                <Typography
                  key={item.label}
                  onClick={() => item.action ? item.action() : handleNavClick(item.href)}
                  sx={{
                    fontFamily: 'Plus Jakarta Sans',
                    fontSize: '13px',
                    color: 'rgba(255,255,255,0.55)',
                    mb: 1.2,
                    cursor: 'pointer',
                    '&:hover': { color: '#4DD9D9' },
                    transition: 'color 0.2s'
                  }}
                >
                  {item.label}
                </Typography>
              ))}
            </Grid>

            {/* Features */}
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography sx={{ fontFamily: 'Outfit', fontWeight: 700, fontSize: '15px', color: '#ffffff', mb: 2.5 }}>Features</Typography>
              {FEATURE_LINKS.map((item) => (
                <Typography
                  key={item.label}
                  onClick={() => handleNavClick(item.href)}
                  sx={{
                    fontFamily: 'Plus Jakarta Sans',
                    fontSize: '13px',
                    color: 'rgba(255,255,255,0.55)',
                    mb: 1.2,
                    cursor: 'pointer',
                    '&:hover': { color: '#4DD9D9' },
                    transition: 'color 0.2s'
                  }}
                >
                  {item.label}
                </Typography>
              ))}
            </Grid>

            {/* Contact Info */}
            <Grid size={{ xs: 12, md: 2.5 }}>
              <Typography sx={{ fontFamily: 'Outfit', fontWeight: 700, fontSize: '15px', color: '#ffffff', mb: 2.5 }}>Contact Us</Typography>
              
              <Box component="a" href="tel:+919069437777" sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center', textDecoration: 'none', '&:hover Typography': { color: '#4DD9D9' } }}>
                <Box sx={{ color: '#4DD9D9', mt: 0.2, flexShrink: 0 }}><PhoneIcon sx={{ fontSize: 16 }} /></Box>
                <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: '13px', color: 'rgba(255,255,255,0.55)', transition: 'color 0.2s' }}>+91 9069437777</Typography>
              </Box>

              <Box component="a" href="mailto:carebeyond@gmail.com" sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center', textDecoration: 'none', '&:hover Typography': { color: '#4DD9D9' } }}>
                <Box sx={{ color: '#4DD9D9', mt: 0.2, flexShrink: 0 }}><EmailIcon sx={{ fontSize: 16 }} /></Box>
                <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: '13px', color: 'rgba(255,255,255,0.55)', transition: 'color 0.2s' }}>carebeyond@gmail.com</Typography>
              </Box>

              <Box component="a" href="https://maps.google.com/?q=Aditya+Empress+Towers+Shaikpet+Hyderabad" target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'flex-start', textDecoration: 'none', '&:hover Typography': { color: '#4DD9D9' } }}>
                <Box sx={{ color: '#4DD9D9', mt: 0.2, flexShrink: 0 }}><LocationOnIcon sx={{ fontSize: 16 }} /></Box>
                <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: '13px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, whiteSpace: 'pre-line', transition: 'color 0.2s' }}>
                  Fairmont 603, Aditya Empress Towers,{"\n"}Shaikpet, Hyderabad - 500008,{"\n"}Telangana, India
                </Typography>
              </Box>
            </Grid>

            {/* Send Us a Message */}
            <Grid size={{ xs: 12, md: 2.5 }}>
              <Typography sx={{ fontFamily: 'Outfit', fontWeight: 700, fontSize: '15px', color: '#ffffff', mb: 2.5 }}>Send Us a Message</Typography>
              {contactSent ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: 'rgba(34, 197, 94, 0.1)', p: 2, borderRadius: '8px' }}>
                  <CheckCircleIcon sx={{ color: '#22c55e' }} />
                  <Typography sx={{ color: '#22c55e', fontFamily: 'Plus Jakarta Sans', fontSize: '13px', fontWeight: 600 }}>Message sent! We'll reach out shortly.</Typography>
                </Box>
              ) : (
                <Box component="form" onSubmit={handleContactSubmit}>
                  <TextField
                    fullWidth placeholder="Your Name" size="small" required
                    value={contactData.name} onChange={(e) => setContactData({ ...contactData, name: e.target.value })}
                    sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: '8px', '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' }, '&:hover fieldset': { borderColor: TEAL }, input: { color: '#fff', fontFamily: 'Plus Jakarta Sans', fontSize: '13px' }, '& input::placeholder': { color: 'rgba(255,255,255,0.4)' } } }}
                  />
                  <TextField
                    fullWidth placeholder="Email Address" type="email" size="small" required
                    value={contactData.email} onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                    sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: '8px', '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' }, '&:hover fieldset': { borderColor: TEAL }, input: { color: '#fff', fontFamily: 'Plus Jakarta Sans', fontSize: '13px' }, '& input::placeholder': { color: 'rgba(255,255,255,0.4)' } } }}
                  />
                  <TextField
                    fullWidth placeholder="Your Message" multiline rows={3} size="small" required
                    value={contactData.message} onChange={(e) => setContactData({ ...contactData, message: e.target.value })}
                    sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: '8px', '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' }, '&:hover fieldset': { borderColor: TEAL }, textarea: { color: '#fff', fontFamily: 'Plus Jakarta Sans', fontSize: '13px' }, '& textarea::placeholder': { color: 'rgba(255,255,255,0.4)' } } }}
                  />
                  <Button type="submit" fullWidth variant="contained" endIcon={<SendIcon sx={{ fontSize: 16 }} />}
                    sx={{ backgroundColor: TEAL, fontFamily: 'Plus Jakarta Sans', fontWeight: 700, fontSize: '13px', py: 1.3, borderRadius: '8px', textTransform: 'none', '&:hover': { backgroundColor: TEAL_DARK } }}>
                    Send Message
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>

          {/* Bottom bar */}
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 3 }} />
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: '12px', color: 'rgba(255,255,255,0.35)' }}>
              © {new Date().getFullYear()} Care Beyond. All rights reserved.
            </Typography>
            <Stack
              direction="row"
              spacing={{ xs: 1.5, sm: 3 }}
              flexWrap="wrap"
              justifyContent="center"
              useFlexGap
            >
              {[
                { label: 'Privacy Policy', href: '/security' },
                { label: 'Terms of Service', href: '/security' },
                { label: 'Security', href: '/security' },
                { label: 'HIPAA Compliance', href: '/security' }
              ].map((l) => (
                <Typography
                  key={l.label}
                  onClick={() => handleNavClick(l.href)}
                  sx={{
                    fontFamily: 'Plus Jakarta Sans',
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.35)',
                    cursor: 'pointer',
                    '&:hover': { color: '#4DD9D9' },
                    transition: 'color 0.2s'
                  }}
                >
                  {l.label}
                </Typography>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Interactive Modal */}
      <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </>
  );
}
